import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { InfoCircle } from "iconsax-react";
import AddDirector from "./AddDirector";
import AddShareholder from "./AddShareholder";
import CompanyContext from "../CompanyContext";
import {
  ArrowForward,
  Avatar,
  BackArrow,
  Edit,
  Info,
  Plus,
  Trash,
} from "../../../assets/svg/AllSvg";
import {
  Badge,
  InformationTag,
  InputWithLabel,
  Tooltip,
} from "../../../components/micro";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  DeleteDirectorShareholder,
  employmentSupport,
  getCorporateSecretary,
  getCurrencies,
} from "../../../redux/companies/slice";
import { getOrganisationDetails } from "../../../redux/organisation/slice";
import { Link } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import RippleLoader from "../../../commons/RippleLoader";
import useShareholderTotals from "../../../hooks/useShareholderTotals";
import ReactSelect from "react-select";
import { InputError } from "../../../commons/MicroComponents";

const LocalResidentDirector = ({
  company_id,
  shareholders,
  resident_directors,
  total_number_of_shares,
  total_issued_share_capital,
  total_paid_up_share_capital,
  currency,
}) => {
  const [showDirectorModel, setShowDirectorModel] = useState(false);
  const [showShareholderModel, setShowShareholderModel] = useState(false);
  const [directorsArray, setDirectorsArray] = useState([]);
  const [shareholderArray, setShareholderArray] = useState([]);
  const [directorsError, setDirectorsError] = useState("");
  const [shareholderError, setShareholderError] = useState("");
  const [deleteLoader, setDeleteLoader] = useState(false);
  const formikRef = useRef(null);
  const [isEdit, setIsEdit] = useState({});
  const [currentTotalShares, setCurrentTotalShares] = useState(
    total_number_of_shares ?? ""
  );

  const dispatch = useDispatch();
  const { prev, next } = useContext(CompanyContext);

  // Selector
  const {
    CorporateSecretaryDetails,
    loading,
    currenciesData,
    currenciesLoading,
  } = useSelector((store) => {
    const currenciesStatus = store?.companiesData?.getCurrenciesStatus;
    // Handle both direct array response and wrapped response
    const currencies =
      currenciesStatus?.data?.data || currenciesStatus?.data || [];
    return {
      loading: store?.companiesData?.loading,
      CorporateSecretaryDetails:
        store?.companiesData?.getCorporateSecretaryStatus?.data?.data,
      currenciesData: Array.isArray(currencies) ? currencies : [],
      currenciesLoading: currenciesStatus?.loading || false,
    };
  });

  // Get Corporate Secretary Details APIs
  useEffect(() => {
    dispatch(getCorporateSecretary());
    dispatch(getCurrencies());
  }, []);

  // set added directors and shareholder array
  useEffect(() => {
    setShareholderArray([...shareholders]);
    setDirectorsArray([...resident_directors]);
  }, [shareholders, resident_directors]);

  // DeleteDirectorShareholder
  const handleDelete = (id, type) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "error",
      type: "warning",
      html: `You won't be able to revert this!`,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((response) => {
      if (response.isConfirmed === true) {
        setDeleteLoader(true);
        dispatch(DeleteDirectorShareholder(id)).then((response) => {
          setDeleteLoader(false);
          if (response.type === "DeleteDirectorShareholder/fulfilled") {
            dispatch(getOrganisationDetails(company_id));
            if (type === "shareholder") {
              const updatedItems = shareholderArray.filter(
                (item) => item.id !== id
              );
              setShareholderArray(updatedItems);
            } else if (type === "directors") {
              const updatedItems = directorsArray.filter(
                (item) => item.id !== id
              );
              setDirectorsArray(updatedItems);
            }
          }
        });
      }
    });
  };

  // Add Director/Shareholder
  const handleSave = (values) => {
    if (!directorsArray.length) {
      setDirectorsError(true);
      return;
    }
    if (!shareholderArray.length) {
      setShareholderError(true);
      return;
    }
    dispatch(
      employmentSupport({
        id: company_id,
        data: {
          total_number_of_shares: values?.total_number_of_shares,
          total_issued_share_capital: values?.total_issued_share_capital,
          total_paid_up_share_capital: values?.total_paid_up_share_capital,
          currency: values?.currency_id,
        },
      })
    )
      .then((res) => {
        if (res.type === "employmentSupport/fulfilled") {
          dispatch(getOrganisationDetails(company_id));
          next();
        }
      })
      .catch((error) => {
        console.error("Error dispatching employmentSupport:", error);
      });
  };

  // set Error on directors and shareholder
  useEffect(() => {
    if (directorsArray.length) {
      setDirectorsError(false);
    }
    if (shareholderArray.length) {
      setShareholderError(false);
    }
  }, [directorsArray, shareholderArray]);

  useShareholderTotals(shareholderArray, formikRef);

  // Currency options for select box
  const currencyOptions = useMemo(() => {
    const currencies = currenciesData || [];
    if (!Array.isArray(currencies) || currencies.length === 0) return [];
    return currencies.map((currency) => ({
      ...currency,
      label: currency.code,
    }));
  }, [currenciesData]);

  // Select styles for ReactSelect
  const getSelectStyles = useCallback(
    (hasError) => ({
      control: (base) => ({
        ...base,
        border: `1px solid ${hasError ? "red" : "#0000003B"}`,
        borderRadius: "0.25rem",
        padding: "6px 4px",
        boxShadow: "none",
        "&:hover": {
          border: "1px solid #0000003B",
        },
      }),
    }),
    []
  );

  return (
    <>
      {loading ? (
        <RippleLoader />
      ) : (
        <div className="space-y-5">
          <div className="flex sm:space-x-4 space-x-2">
            <Info className="sm:max-w-5 max-w-4 w-full" />
            <div className="space-y-3 lg:text-base sm:text-sm text-xs">
              <p>
                Singapore law mandates that all incorporated companies must have
                at least one local resident director and one shareholder.
              </p>
              <p>
                You can use your own Local Director (Singaporean Citizen, PR or
                Entrepass holder) or sign up for our Nominee Local Director
                service.
              </p>
              <Link
                target="_blank"
                to={
                  "https://www.acra.gov.sg/how-to-guides/foreigners-registering-a-business-in-singapore/requirements-for-local-residency"
                }
                className="text-theme border-b border-theme responsive_base_text"
              >
                Read More
              </Link>
            </div>
          </div>
          <div className="sm:space-y-3 space-y-2 border-b border-secondary_grey sm:pb-5 pb-3">
            <p className="lg:text-lg sm:text-base text-sm">1. Directors</p>
            <p className="lg:text-base sm:text-sm text-xs">
              An email invitation will be sent to company members for identity
              verification.
            </p>
            {directorsError && (
              <InformationTag
                text={
                  "At least 1 Operational Director (Non Nominee Director) must be appointed"
                }
                type={"error"}
              />
            )}
            <div className="lg:grid lg:grid-cols-4 gap-5 bg-light_secondary_shades rounded-lg py-4 px-5">
              <div className="lg:border-r-2 pr-4 border-b-2 lg:border-b-0">
                <p className="lg:text-xl sm:text-base text-sm font-ebGaramond font-semibold mb-2">
                  Directors
                </p>
                <p className="text-light_grey font-medium sm:text-sm text-xs pb-2">
                  At least 1 local resident director required
                </p>
              </div>
              <div className="col-span-3">
                <div
                  className={`space-y-4 max-h-80 overflow-y-auto mb-4 ${
                    directorsArray?.length && "p-3"
                  }`}
                >
                  {directorsArray?.map((element) => {
                    return (
                      <div
                        className={`card bg-white border justify-between items-center w-full ${
                          element?.main_signee ? "sm:flex" : "flex"
                        }`}
                      >
                        <div className="flex items-center space-x-3 max-w-[calc(100%-80px)] w-full">
                          <div className="sm:max-w-11 sm:max-h-11 max-w-6 max-h-max-w-6 w-full h-full">
                            <Avatar svgClass="w-full h-full" />
                          </div>
                          <div className="w-full">
                            <p className="sm:text-lg text-xs font-medium">
                              {element?.user?.full_name}
                            </p>
                            <p className="sm:text-sm text-xs max-w-full truncate">
                              {element?.user?.email}
                            </p>
                          </div>
                        </div>
                        {element?.main_signee ? (
                          <Badge
                            value={"Main Signee"}
                            className={
                              "border border-theme whitespace-nowrap text-theme sm:text-sm text-xs font-semibold sm:!py-2 !py-1 sm:px-4 px-2"
                            }
                          />
                        ) : null}
                        <div className="flex justify-between items-center text-center">
                          <button
                            className="p-3"
                            onClick={() => {
                              setShowDirectorModel(true);
                              setIsEdit(element);
                            }}
                          >
                            <Edit width="16" height="20" />
                          </button>
                          <button
                            className="p-3"
                            disabled={deleteLoader}
                            onClick={() => {
                              handleDelete(element?.id, "directors");
                            }}
                          >
                            <Trash width="16" height="20" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="btn-outline uppercase flex items-center sm:text-base text-sm ml-3"
                  onClick={() => {
                    setShowDirectorModel(true);
                    setIsEdit({});
                  }}
                >
                  <Plus className="fill-theme mr-2" />
                  Add director
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-3 border-b border-secondary_grey pb-5">
            <p className="lg:text-lg sm:text-base text-sm">2. Shareholders</p>
            <p className="lg:text-base sm:text-sm text-xs">
              An email invitation will be sent to company members for identity
              verification.
            </p>
            {shareholderError && (
              <InformationTag
                text={"At least 1 Shareholder must be appointed"}
                type={"error"}
              />
            )}
            <div className="lg:grid lg:grid-cols-4 lg:gap-5 bg-light_secondary_shades rounded-lg py-4 px-5">
              <div className="lg:border-r-2 pr-4 border-b-2 lg:border-b-0">
                <p className="lg:text-xl sm:text-base text-sm font-ebGaramond font-semibold  flex items-center mb-2">
                  Shareholders{" "}
                  <Tooltip
                    label={
                      <>
                        <InfoCircle className="text-theme xl:w-6 xl:h-6 w-4 h-4 ml-1" />
                      </>
                    }
                    tooltipLabel={
                      <>
                        <div className="whitespace-nowrap sm:text-base text-sm">
                          Pricing:
                          <li>
                            1 Shareholder: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholder_1
                            }
                            /year
                          </li>
                          <li>
                            2 Shareholders: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholders_2
                            }
                            /year
                          </li>
                          <li>
                            3 - 5 Shareholders: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholders_3_5
                            }
                            /year
                          </li>
                          <li>
                            6 - 9 Shareholders: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholders_6_9
                            }
                            /year
                          </li>
                          <li>
                            10 - 20 Shareholders: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholders_10_20
                            }
                            /year
                          </li>
                          <li>
                            21- 30 Shareholders: $
                            {
                              CorporateSecretaryDetails?.detail?.tiered_pricing
                                ?.shareholders_21_30
                            }
                            /year
                          </li>
                          <li>{`>30 Shareholders: $${CorporateSecretaryDetails?.detail?.tiered_pricing?.greater_than_30_shareholders}/year`}</li>
                        </div>
                      </>
                    }
                  />
                </p>
                {/* <p className="text-light_grey font-medium sm:text-sm text-xs">
                  Starting at SGD 300 per year for corporate secretary services
                </p> */}
              </div>
              <div className="col-span-3">
                <div
                  className={`space-y-4 max-h-80 overflow-y-auto mb-4 ${
                    shareholderArray?.length && "p-3"
                  }`}
                >
                  {shareholderArray?.map((element) => {
                    return (
                      <div className="card bg-white border flex justify-between items-start">
                        <div className="flex items-start space-x-3 max-w-[calc(100%-80px)] w-full">
                          <div className="sm:max-w-11 sm:max-h-11 max-w-6 max-h-max-w-6 w-full h-full">
                            <Avatar svgClass="w-full h-full" />
                          </div>
                          <div className="w-full">
                            <p className="sm:text-lg text-xs font-medium">
                              {element?.user?.full_name}
                            </p>
                            <p className="sm:text-sm text-xs max-w-full truncate mb-1">
                              {element?.user?.email}
                            </p>
                            <div className="grid grid-cols-2 gap-1">
                              <p className="text-xs text-gray-400 max-w-full truncate">
                                Number of Shares:{" "}
                                <span className="text-black">
                                  {element?.number_of_shares ?? 0}
                                </span>
                              </p>
                              <p className="text-xs text-gray-400 max-w-full truncate">
                                Issued Share Capital:{" "}
                                <span className="text-black">
                                  {element?.issued_share_capital ?? 0}
                                </span>
                              </p>
                              <p className="text-xs text-gray-400 max-w-full truncate">
                                Paid-up Share Capital:{" "}
                                <span className="text-black">
                                  {element?.paid_up_capital ?? 0}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <button
                          className="p-3"
                          onClick={() => {
                            setShowShareholderModel(true);
                            setIsEdit(element);
                          }}
                        >
                          <Edit width="16" height="20" />
                        </button>
                        <button
                          className="p-3"
                          disabled={deleteLoader}
                          onClick={() => {
                            handleDelete(element?.id, "shareholder");
                          }}
                        >
                          <Trash width="16" height="20" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="btn-outline uppercase flex items-center sm:text-base text-sm"
                  onClick={() => {
                    setShowShareholderModel(true);
                    setIsEdit({});
                  }}
                >
                  <Plus className="fill-theme mr-2" />
                  Add shareholder
                </button>
              </div>
            </div>
          </div>
          <Formik
            enableReinitialize={true}
            initialValues={{
              // Overall company share fields
              total_number_of_shares: total_number_of_shares ?? "",
              total_issued_share_capital: total_issued_share_capital ?? "",
              total_paid_up_share_capital: total_paid_up_share_capital ?? "",
              currency_id: currency ?? "",
            }}
            validationSchema={Yup.object().shape({
              // Overall company share validation
              total_number_of_shares: Yup.number()
                .typeError("Total Number of Shares must be a valid number")
                .min(0, "Total Number of Shares must be 0 or greater")
                .required("Please Enter Total Number of Shares"),
              total_issued_share_capital: Yup.number()
                .typeError("Total Issued Share Capital must be a valid number")
                .min(0, "Total Issued Share Capital must be 0 or greater")
                .required("Please Enter Total Issued Share Capital"),
              total_paid_up_share_capital: Yup.number()
                .typeError("Total Paid-up Share Capital must be a valid number")
                .min(0, "Total Paid-up Share Capital must be 0 or greater")
                .required("Please Enter Total Paid-up Share Capital"),
              currency_id: Yup.string().required("Please select a currency"),
            })}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => {
              if (formikRef.current?.setFieldValue !== setFieldValue) {
                formikRef.current = { setFieldValue };
              }
              // Update currentTotalShares when form values change
              if (currentTotalShares !== values.total_number_of_shares) {
                setCurrentTotalShares(values.total_number_of_shares);
              }
              return (
                <Form className="w-full space-y-3" onSubmit={handleSubmit}>
                  <div>
                    <p className="lg:text-lg sm:text-base text-sm">
                      4. Share Capital
                    </p>
                    <p className="responsive_base_text">
                      Overall Company Share Information
                    </p>
                    <div className="grid xl:grid-cols-4 lg:grid-cols-2 lg:gap-5 gap-3 bg-light_secondary_shades rounded-lg py-4 px-5">
                      <InputWithLabel
                        label={"Total Number of Shares"}
                        type={"number"}
                        className={
                          errors.total_number_of_shares &&
                          touched.total_number_of_shares
                            ? ` input-error `
                            : ` border `
                        }
                        idFromName="total_number_of_shares"
                        disabled={true}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.total_number_of_shares}
                        errors={
                          errors.total_number_of_shares &&
                          touched.total_number_of_shares
                        }
                        errorsText={errors.total_number_of_shares}
                      />

                      <InputWithLabel
                        label={"Total Issued Share Capital"}
                        type={"number"}
                        className={
                          errors.total_issued_share_capital &&
                          touched.total_issued_share_capital
                            ? ` input-error `
                            : ` border `
                        }
                        idFromName="total_issued_share_capital"
                        disabled={true}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.total_issued_share_capital}
                        errors={
                          errors.total_issued_share_capital &&
                          touched.total_issued_share_capital
                        }
                        errorsText={errors.total_issued_share_capital}
                      />

                      <InputWithLabel
                        label={"Total Paid-up Share Capital"}
                        type={"number"}
                        className={
                          errors.total_paid_up_share_capital &&
                          touched.total_paid_up_share_capital
                            ? ` input-error `
                            : ` border `
                        }
                        idFromName="total_paid_up_share_capital"
                        disabled={true}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.total_paid_up_share_capital}
                        errors={
                          errors.total_paid_up_share_capital &&
                          touched.total_paid_up_share_capital
                        }
                        errorsText={errors.total_paid_up_share_capital}
                      />

                      <div>
                        <ReactSelect
                          className="text-black sm:text-base text-sm p-0"
                          id="currency_id"
                          placeholder="Select Currency"
                          options={currencyOptions}
                          isLoading={currenciesLoading}
                          getOptionValue={(option) => option.code}
                          value={currencyOptions.find(
                            (op) => op.code === values.currency_id
                          )}
                          onBlur={handleBlur}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "currency_id",
                              selectedOption ? selectedOption.code : ""
                            );
                          }}
                          styles={getSelectStyles(
                            !!(errors.currency_id && touched.currency_id)
                          )}
                        />
                        {errors.currency_id && touched.currency_id && (
                          <InputError errorTitle={errors.currency_id} />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-4 pt-3">
                    <button
                      type="button"
                      className="btn-outline flex items-center space-x-3 uppercase"
                      onClick={() => prev()}
                    >
                      <BackArrow className="fill-theme" fillOpacity={1} />
                      <span>Back</span>
                    </button>
                    <button
                      type="submit"
                      className="btn-theme flex items-center space-x-3 uppercase"
                    >
                      <span>save & proceed on</span> <ArrowForward />
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
      {showDirectorModel && (
        <AddDirector
          setShowDirectorModel={setShowDirectorModel}
          showDirectorModel={showDirectorModel}
          company_id={company_id}
          setDirectorsArray={setDirectorsArray}
          directorsArray={directorsArray}
          isEdit={isEdit}
        />
      )}
      {showShareholderModel && (
        <AddShareholder
          setShowShareholderModel={setShowShareholderModel}
          shareholderArray={shareholderArray}
          setShareholderArray={setShareholderArray}
          company_id={company_id}
          isEdit={isEdit}
          total_number_of_shares={currentTotalShares}
        />
      )}
    </>
  );
};

export default LocalResidentDirector;
